import type { WalkthroughStep } from "../types";

/**
 * Clash Finder's tour, which only ever runs because somebody asked for it.
 *
 * The island mounts this in manual mode (`openSignal`), so nothing here is ever
 * thrown over a report a user produced from their own folder. That decides what
 * the steps may say: every one of them can talk about a result that is on screen
 * as it is read, because the tour opens from a click, never from a report
 * appearing.
 *
 * The `welcome` step announces the sample and is dropped by the island when no
 * sample is loaded, because "not your files" is false over a folder the reader
 * picked themselves.
 *
 * Also reachable without loading a sample. The island renders a permanent
 * "Show me how this works" control in its intake row (`ShowTourButton`), which
 * raises the same signal, because gating every route to the tour on the demo
 * button left the tool's actual user — somebody who dropped their own folder —
 * with no tutorial and no way to ask for one. So a step may still assume a
 * result is on screen, and may no longer assume that result came from us:
 * anything that describes the sample by name says so explicitly rather than
 * relying on there being no other way in.
 *
 * These steps RESTATE what the island already says; they absorb nothing. The
 * scope note, the "not compared" verdict and the privacy sentence all stay in
 * the interface, because a limit stated only in a tutorial nobody opened is a
 * limit not stated — and the tour renders for exactly one kind of user.
 */
export const steps: WalkthroughStep[] = [
  {
    id: "welcome",
    title: "This is a sample pack",
    body: "Two vendor packs are loaded — not your files, and every export says so. Six stops, then drop your own resources folder.",
    placement: "center",
  },
  {
    id: "drop",
    title: "Where your folder goes",
    body: "Both packs ship the same internal paths, and they stayed apart: the report names the archive each copy came from. Drop your own `resources/` folder here, or the zips you bought.",
    selector: "[data-walkthrough='clf-drop']",
    placement: "bottom",
  },
  {
    id: "cfg",
    title: "Start order is the answer",
    body: "Which copy of a colliding asset the game keeps is decided by `ensure` order and nothing else. Without a server.cfg this tool says load order unknown on every collision rather than guessing.",
    selector: "[data-walkthrough='clf-cfg']",
    placement: "top",
  },
  {
    id: "bytes",
    title: "Names first, bytes second",
    body: "The list above was built without reading a byte. This pass hashes only the files that actually collide — a vendor's own shared wheel is harmless, a genuine override is content you paid for never appearing.",
    selector: "[data-walkthrough='clf-bytes']",
    placement: "top",
  },
  {
    id: "contests",
    title: "One card per contested name",
    body: "Worst first, emergency fleet above everything else. Each card names every copy, the archive it came from, and the verdict — including not compared, which is a statement about the check and not about the files.",
    selector: "[data-walkthrough='clf-contests']",
    placement: "top",
  },
  {
    id: "findings",
    title: "What was not checked",
    body: "Everything the read refused, capped or skipped is listed here in words, next to the findings. A clean list means the checks that ran found nothing — not that nothing is wrong.",
    selector: "[data-walkthrough='clf-findings']",
    placement: "top",
  },
  {
    id: "exports",
    title: "Take it to the vendor",
    body: "Markdown, CSV and JSON carry every row, not just the cards drawn on screen, plus the not-checked list. A sample-pack report downloads with a sample- prefix so it can never be mistaken for an audit of a real server.",
    selector: "[data-walkthrough='clf-exports']",
    placement: "top",
  },
];

export const finishLabel = "Drop my own folder";
